import { useEffect, useRef, useState } from "react";
import { useStore } from "zustand";
import * as commands from "../commands";
import { buildQuoteRequest, formatCents } from "../core";
import type { EvidenceSource, Product, ProductCandidate, Requirement, RequirementMatch } from "../core/types";
import { DEMO_REQUIREMENTS, SPEC_REQUIREMENTS } from "../demo/requirements";
import { FE1_OPTIONS, FE1_SPEC_ISSUES } from "../demo/spec-options";
import type { AppStore, CompatibleCandidate } from "../store/store";

export type ToolSummary = { id: string; summary: string; effect: string };
export type AppProps = { store: AppStore; merchant: string; project: string; catalog: Product[]; tools: ToolSummary[]; onClose?: () => void };

const REASON: Record<string, string> = {
  attribute_missing: "not on file for this product",
  malformed_value: "value could not be read",
  not_a_product_attribute: "depends on the installation, not the product",
  unsupported_operator: "comparison not supported",
  unit_mismatch: "units differ",
  pair_check_required: "decided by the fit check",
};
const OP: Record<string, string> = { eq: "", ne: "not", gte: "at least", lte: "at most", one_of: "one of", not_one_of: "not", meets_rating: "at least", is_true: "" };
const SOURCE_WORD: Record<Requirement["source"]["kind"], string> = { spec: "spec", code: "code", schedule: "schedule" };

const words = (attr: string) => attr.replace(/_in$/, " (in)").replace(/_lb$/, " (lb)").replace(/_gal$/, " (gal)").replace(/_/g, " ");
const reqText = (r: Requirement) => {
  const v = r.value === true ? "" : Array.isArray(r.value) ? r.value.join(", ") : String(r.value);
  return `${words(r.attribute)}${OP[r.operator] ? ` ${OP[r.operator]}` : ""}${v ? ` ${v}` : ""}${r.unit && !r.attribute.endsWith(`_${r.unit}`) ? ` ${r.unit}` : ""}`.replace(/\s+/g, " ").trim();
};
const cite = (r: Requirement) => `${SOURCE_WORD[r.source.kind]}${r.source.section ? ` §${r.source.section}` : r.source.table ? ` table ${r.source.table}` : ""}`;
const evidenceWord = (e?: EvidenceSource) => (!e ? "" : e.kind === "manufacturer_datasheet" ? `cut sheet${e.page ? ` p.${e.page}` : ""}` : e.kind === "merchant_guide" ? "selection guide" : "product page");
const fmt = (v: unknown, unit?: string) => (v === undefined ? "" : typeof v === "boolean" ? (v ? "yes" : "no") : `${String(v)}${unit ? ` ${unit}` : ""}`);

function Thumb({ p, size = 44 }: { p?: Product; size?: number }) {
  if (!p?.imageUrl) return <span className="sc-thumb sc-thumb-empty" style={{ width: size, height: size }} />;
  return <img className="sc-thumb" src={p.imageUrl} alt="" width={size} height={size} decoding="async" />;
}

function Dot({ s }: { s: "satisfied" | "conflict" | "unknown" | "approved" | "rejected" | "proposed" }) {
  return <span className={`sc-dot2 ${s}`} aria-hidden="true" />;
}

function Ev({ e }: { e?: EvidenceSource }) {
  if (!e) return null;
  const w = evidenceWord(e);
  return e.url ? <a className="sc-ev" href={e.url} target="_blank" rel="noreferrer">{w}</a> : <span className="sc-ev">{w}</span>;
}

export function App({ store, catalog, tools, onClose }: AppProps) {
  const product = useStore(store, (s) => s.product);
  const requirements = useStore(store, (s) => s.requirements);
  const matrix = useStore(store, (s) => s.matrix);
  const alternatives = useStore(store, (s) => s.alternatives);
  const compatible = useStore(store, (s) => s.compatible);
  const resolution = useStore(store, (s) => s.resolution);
  const specResolution = useStore(store, (s) => s.specResolution);
  const specOptions = useStore(store, (s) => s.specOptions);
  const specIssues = useStore(store, (s) => s.specIssues);
  const specRequirements = useStore(store, (s) => s.specRequirements);
  const quoteLines = useStore(store, (s) => s.quoteLines);
  const notes = useStore(store, (s) => s.notes);
  const log = useStore(store, (s) => s.log);
  const webmcp = useStore(store, (s) => s.webmcp);
  const topRef = useRef<HTMLDivElement>(null);
  const [flash, setFlash] = useState(false);

  const bySku = (s: string) => catalog.find((p) => p.sku === s);
  const reqById = new Map([...requirements, ...specRequirements].map((r) => [r.id, r]));
  const quote = buildQuoteRequest(quoteLines, catalog);
  const toolsOn = webmcp.api !== "none";
  const waitingLines = quoteLines.filter((l) => l.status === "proposed");
  const decidedLines = quoteLines.filter((l) => l.status !== "proposed");
  const lastWaiting = useRef(0);
  useEffect(() => {
    if (waitingLines.length > lastWaiting.current) {
      const scroller = topRef.current?.closest(".sc-drawer");
      if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" });
      else topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1600);
      lastWaiting.current = waitingLines.length;
      return () => clearTimeout(t);
    }
    lastWaiting.current = waitingLines.length;
  }, [waitingLines.length]);

  if (!product) {
    return (
      <div>
        <Head title="Spec check" sub="Not a product this page knows" toolsOn={false} onClose={onClose} />
        <p className="sc-empty2">There is nothing to check on this page.</p>
      </div>
    );
  }

  const otherFamily = product.family === "portable_fire_extinguisher" ? "fire_extinguisher_cabinet" : "portable_fire_extinguisher";
  const loadDemo = () => {
    if (product.family === "portable_fire_extinguisher") commands.resolveSpec(store, catalog, product.family, FE1_OPTIONS, "human", FE1_SPEC_ISSUES);
    else commands.resolve(store, catalog, product.family, DEMO_REQUIREMENTS.filter((r) => r.appliesTo === product.family), "human");
    commands.checkRequirements(store, catalog, DEMO_REQUIREMENTS, "human");
    commands.findCompatible(store, catalog, otherFamily, SPEC_REQUIREMENTS, "human");
  };
  const anything = !!(matrix || resolution || specResolution || compatible || quoteLines.length);

  return (
    <div>
      <Head title="Spec check" sub={`${product.name}${product.priceCents !== null ? ` · ${formatCents(product.priceCents)}` : ""}`} toolsOn={toolsOn} connected={log.some((e) => e.source === "agent")} onClose={onClose} />
      <div ref={topRef} />

      {waitingLines.length > 0 && (
        <section className={`sc-card sc-needs ${flash ? "sc-flash" : ""}`}>
          <h3>Needs you · {waitingLines.length} line{waitingLines.length === 1 ? "" : "s"} to approve</h3>
          {waitingLines.map((l) => <WaitingLine key={l.id} l={l} p={bySku(l.sku)} store={store} />)}
          <p className="sc-hint">Nothing is ordered. Approved lines go on a quote request you send later. Change a quantity or reject with a reason and your agent sees it.</p>
        </section>
      )}

      {!anything && (
        <section className="sc-card">
          <p className="sc-empty2">
            Use a spec section, plans, a schedule, or other construction documents to find compliant products. Results and lines that need your approval show up here.
          </p>
          <button className="sc-link sc-noprint" onClick={loadDemo}>Try it with the demo documents</button>
        </section>
      )}

      {specIssues.length > 0 && (
        <section className="sc-card sc-muted">
          <h3>Flagged in your documents</h3>
          {specIssues.map((t, i) => <p key={i} className="sc-issue">{t}</p>)}
          <p className="sc-hint">Noted while reading your documents. Not a verdict.</p>
        </section>
      )}

      {specResolution && (
        <section className="sc-card">
          <h3>What satisfies your spec</h3>
          {specResolution.options.map((o) => {
            const meta = specOptions.find((x) => x.id === o.optionId);
            const bod = o.basisOfDesign;
            const name = (s: string) => bySku(s)?.name ?? s;
            const price = (s: string) => (bySku(s)?.priceCents != null ? ` · ${formatCents(bySku(s)!.priceCents!)}` : "");
            return (
              <div key={o.optionId} className="sc-clause">
                <b>{(meta?.label ?? o.optionId).replace(/:\s.*$/, (m) => (m.length > 60 ? ": " + m.slice(2, 58).trim() + "…" : m))}</b>
                <ul className="sc-list">
                  {bod && <li><Dot s={bod.carried ? "satisfied" : "unknown"} /><span>{bod.requested.manufacturer} {bod.requested.model}: {bod.carried ? `carried as ${bod.carried.name}` : "not carried on this site"}</span></li>}
                  {o.permitted.map((c) => <li key={c.sku}><Dot s="satisfied" /><Thumb p={bySku(c.sku)} /><span><a href={bySku(c.sku)?.url} target="_blank" rel="noreferrer">{name(c.sku)}</a>{price(c.sku)} — permitted by this clause</span></li>)}
                  {o.technicalMatches.map((c) => <li key={c.sku}><Dot s="unknown" /><Thumb p={bySku(c.sku)} /><span><a href={bySku(c.sku)?.url} target="_blank" rel="noreferrer">{name(c.sku)}</a>{price(c.sku)} — matches the numbers, not the named model. Needs a substitution approval.{c.counts.unknown ? ` ${c.counts.unknown} requirement${c.counts.unknown === 1 ? "" : "s"} not verifiable from product data: ${c.matches.filter((m) => m.status === "unknown").map((m) => words(reqById.get(m.requirementId)?.attribute ?? m.requirementId)).join(", ")}.` : ""}</span></li>)}
                  {o.possible.map((c) => <li key={c.sku}><Dot s="unknown" /><span>{name(c.sku)}{price(c.sku)} — no conflict, {c.counts.unknown} unresolved</span></li>)}
                  {o.kind === "assembly" && (o.assemblies ?? []).map((a, i) => <li key={i}><Dot s="satisfied" /><span className="sc-thumbs">{a.products.map((s) => <Thumb key={s} p={bySku(s)} />)}</span><span>{a.products.map((s) => name(s)).join(" + ")} — together they satisfy this clause{a.products.map(price).join("")}</span></li>)}
                  {o.kind === "assembly" && (o.assemblies ?? []).length === 0 && <li><Dot s="conflict" /><span>No combination on this site fills every part of this clause.</span></li>}
                  {o.kind !== "assembly" && o.permitted.length + o.technicalMatches.length + o.possible.length === 0 && <li><Dot s="conflict" /><span>Nothing on this site satisfies this clause.</span></li>}
                </ul>
                {(o.rejected.length > 0 || (o.slots ?? []).some((s) => s.rejected.length)) && (
                  <details className="sc-more">
                    <summary>Why the others don't</summary>
                    <ul className="sc-list">
                      {o.rejected.map((r) => <li key={r.candidate.sku}><Dot s="conflict" /><span>{name(r.candidate.sku)}: {r.reasons.map(short).join("; ")}</span></li>)}
                      {(o.slots ?? []).flatMap((s) => s.rejected.map((r) => <li key={`${s.slotId}-${r.candidate.sku}`}><Dot s="conflict" /><span>{name(r.candidate.sku)} as {s.slotId}: {r.reasons.map(short).join("; ")}</span></li>))}
                    </ul>
                  </details>
                )}
              </div>
            );
          })}
        </section>
      )}

      {!specResolution && resolution && (
        <section className="sc-card">
          <h3>What satisfies your requirements</h3>
          <ul className="sc-list">
            {resolution.matches.map((c) => <li key={c.sku}><Dot s="satisfied" /><Thumb p={bySku(c.sku)} /><span><a href={bySku(c.sku)?.url} target="_blank" rel="noreferrer">{bySku(c.sku)?.name ?? c.sku}</a> — every requirement verified</span></li>)}
            {resolution.possible.map((c) => <li key={c.sku}><Dot s="unknown" /><span>{bySku(c.sku)?.name ?? c.sku} — no conflict, {c.counts.unknown} unresolved</span></li>)}
            {resolution.matches.length + resolution.possible.length === 0 && <li><Dot s="conflict" /><span>Nothing on this site satisfies all of them.</span></li>}
          </ul>
          {resolution.rejected.length > 0 && (
            <details className="sc-more"><summary>Why the others don't ({resolution.rejected.length})</summary>
              <ul className="sc-list">{resolution.rejected.map((r) => <li key={r.candidate.sku}><Dot s="conflict" /><span>{bySku(r.candidate.sku)?.name ?? r.candidate.sku}: {r.reasons.map(short).join("; ")}</span></li>)}</ul>
            </details>
          )}
        </section>
      )}

      {matrix && (
        <section className="sc-card">
          <h3>This product vs. your requirements</h3>
          <div className="sc-counts">
            <span className="sc-status satisfied">{matrix.counts.satisfied} verified</span>
            {matrix.counts.conflict > 0 && <span className="sc-status conflict">{matrix.counts.conflict} conflict</span>}
            {matrix.counts.unknown > 0 && <span className="sc-status unknown">{matrix.counts.unknown} unresolved</span>}
          </div>
          <ul className="sc-list">
            {matrix.matches.map((m) => <ReqRow key={m.requirementId} m={m} r={reqById.get(m.requirementId)} />)}
          </ul>
          {alternatives.length > 0 && (
            <p className="sc-hint">
              Also no conflict: {alternatives.map(({ sku: s, counts }, i) => { const p = bySku(s); return p ? <span key={s}>{i ? "; " : ""}<a href={p.url} target="_blank" rel="noreferrer">{p.name}</a>{p.priceCents != null ? ` (${formatCents(p.priceCents)})` : ""}{counts.unknown ? `, ${counts.unknown} unresolved` : ""}</span> : null; })}
            </p>
          )}
        </section>
      )}

      {compatible && <FitCard compatible={compatible} bySku={bySku} product={product} />}

      {decidedLines.length > 0 && (
        <section className="sc-card">
          <h3>Quote request</h3>
          <ul className="sc-list">
            {decidedLines.map((l) => {
              const p = bySku(l.sku); const priced = quote.lines.find((x) => x.id === l.id);
              return <li key={l.id}><Dot s={l.status} /><Thumb p={p} size={36} /><span className="sc-grow">{p?.name ?? l.sku} · {l.quantity} {l.unit}{l.decisionNote && <span className="sc-why">You: “{l.decisionNote}”</span>}</span><span className="sc-amt">{l.status === "approved" ? (priced?.extendedCents != null ? formatCents(priced.extendedCents) : "not priced") : "rejected"}</span></li>;
            })}
          </ul>
          <div className="sc-subtotal"><span>Subtotal, approved lines</span><b>{formatCents(quote.subtotalCents)}</b></div>
          <p className="sc-hint"><b>Not submitted.</b> Indicative list prices; this is a quote request, not a quotation.</p>
        </section>
      )}

      {anything && <NoteBox store={store} notes={notes} />}

      <footer className="sc-foot sc-noprint">
        <details><summary>Tools on this page ({tools.length}) · {toolsOn ? "on" : "off in this browser"}</summary>
          <ul className="sc-list sc-small">{tools.map((t) => <li key={t.id}><code>{t.id}</code><span>{t.summary}</span></li>)}</ul>
        </details>
        <details><summary>Activity ({log.length})</summary>
          <div className="sc-log">{log.map((e, i) => <div key={i}>{e.at.slice(11, 19)} · {e.source === "agent" ? "agent" : e.source === "human" ? "you" : "page"} · {e.message}</div>)}</div>
        </details>
        <div className="sc-row">
          {anything && <button className="sc-link" onClick={() => commands.reset(store)}>Start over</button>}
          {anything && <button className="sc-link" onClick={() => window.print()}>Print</button>}
          {!anything && null}
        </div>
      </footer>
    </div>
  );
}

function WaitingLine({ l, p, store }: { l: { id: string; sku: string; quantity: number; unit: string; quantitySource: { kind: string; sheet?: string }; note?: string; proposedBy: string }; p?: Product; store: AppStore }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [qtyText, setQtyText] = useState(String(l.quantity));
  useEffect(() => setQtyText(String(l.quantity)), [l.quantity]);
  const commitQty = (raw: string) => {
    setQtyText(raw);
    const n = Number(raw);
    if (raw.trim() !== "" && Number.isInteger(n) && n >= 1 && n <= 999) commands.setLineQuantity(store, l.id, n);
  };
  return (
    <div className="sc-linecard">
      <Thumb p={p} size={56} />
      <div className="sc-linemain">
        <b>{p?.name ?? l.sku}</b>
        <span>
          <input className="sc-qty" type="number" min={1} max={999} value={qtyText} aria-label="Quantity"
            onChange={(e) => commitQty(e.target.value)}
            onBlur={() => setQtyText(String(l.quantity))} />
          {l.unit} · from {l.quantitySource.kind.replace("_", " ")}{l.quantitySource.sheet ? ` ${l.quantitySource.sheet}` : ""}{p?.priceCents != null ? ` · ${formatCents(p.priceCents)} each` : ""}
        </span>
        {l.note && <span className="sc-agentnote">{l.proposedBy === "agent" ? "Your agent" : "You"}: “{l.note}”</span>}
        {rejecting && (
          <form className="sc-row sc-reason" onSubmit={(e) => { e.preventDefault(); commands.rejectQuoteLine(store, l.id, reason); }}>
            <input autoFocus value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Tell your agent why (optional)" maxLength={200} aria-label="Reason" />
            <button className="sc-btn" type="submit">Reject</button>
            <button className="sc-link" type="button" onClick={() => setRejecting(false)}>Cancel</button>
          </form>
        )}
      </div>
      {!rejecting && (
        <div className="sc-row sc-noprint">
          <button className="sc-btn primary" onClick={() => commands.approveQuoteLine(store, l.id)}>Approve</button>
          <button className="sc-btn" onClick={() => setRejecting(true)}>Reject</button>
        </div>
      )}
    </div>
  );
}

function NoteBox({ store, notes }: { store: AppStore; notes: { id: string; text: string; at: string }[] }) {
  const [text, setText] = useState("");
  return (
    <section className="sc-card sc-notebox">
      <h3>Note for your agent</h3>
      {notes.length > 0 && <ul className="sc-list">{notes.map((n) => <li key={n.id}><Dot s="unknown" /><span>“{n.text}” <span className="sc-cite2">{n.at.slice(11, 16)}</span></span></li>)}</ul>}
      <form className="sc-row sc-noprint" onSubmit={(e) => { e.preventDefault(); if (commands.addNote(store, text)) setText(""); }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="e.g. we're in masonry, recessed cabinets only" maxLength={500} aria-label="Note for your agent" />
        <button className="sc-btn" type="submit">Leave note</button>
      </form>
      <p className="sc-hint">Your agent reads notes the next time it checks this page.</p>
    </section>
  );
}

function Head({ title, sub, toolsOn, connected, onClose }: { title: string; sub: string; toolsOn: boolean; connected?: boolean; onClose?: () => void }) {
  return (
    <div className="sc-head">
      <div>
        <h2>{title}</h2>
        <div className="sc-sub">{sub}</div>
      </div>
      <span className={`sc-tools ${toolsOn ? (connected ? "live" : "on") : ""}`} title={toolsOn ? (connected ? "An agent has used this page's WebMCP tools" : "This page registered WebMCP tools; an agent in this browser can use them") : "This browser does not expose WebMCP"}>
        <span className="sc-dot2 mini" /> {toolsOn ? (connected ? "WebMCP connected" : "WebMCP active") : "WebMCP unavailable"}
      </span>
      {onClose && <button type="button" className="sc-btn sc-close" onClick={onClose} aria-label="Close">×</button>}
    </div>
  );
}

function short(reason: string): string {
  // "spec-2.2-c: 1-A:10-B:C vs 2-A: 1-A < 2-A" -> "1-A < 2-A"
  const afterId = reason.replace(/^[^:]+:\s*/, "");
  const parts = afterId.split(": ");
  return parts[parts.length - 1] ?? afterId;
}

function ReqRow({ m, r }: { m: RequirementMatch; r?: Requirement }) {
  const what = r ? reqText(r) : m.requirementId;
  const why = m.status === "satisfied" ? fmt(m.actual, m.unit) : m.status === "conflict" ? (m.detail ? short(m.detail) : "conflict") : (REASON[m.reason ?? ""] ?? m.detail ?? "unresolved");
  return (
    <li>
      <Dot s={m.status} />
      <span className="sc-grow">
        {what} <span className="sc-cite2">({r ? cite(r) : ""})</span>
        <span className="sc-why">{m.status === "satisfied" ? "verified" : m.status === "conflict" ? "conflict" : "unresolved"}: {why} <Ev e={m.evidence} /></span>
      </span>
    </li>
  );
}

function FitCard({ compatible, bySku, product }: { compatible: { lookingFor: string; candidates: CompatibleCandidate[] }; bySku: (s: string) => Product | undefined; product: Product }) {
  const good = compatible.candidates.filter((c) => c.fit?.status === "satisfied" && c.candidate.counts.conflict === 0);
  const rest = compatible.candidates.filter((c) => !good.includes(c));
  const title = product.family === "portable_fire_extinguisher" ? "Cabinets that fit this extinguisher" : "Extinguishers that fit this cabinet";
  const depth = (c: CompatibleCandidate) => c.fit?.clearances.find((x) => x.dimension === "diameter_vs_depth");
  const fitWords = (c: CompatibleCandidate) => {
    const d = depth(c);
    if (c.fit?.status === "unknown") return "fit unknown: interior not on file";
    if (d) return d.clearanceIn > 0 ? `fits, ${d.clearanceIn} in to spare` : `too shallow by ${Math.abs(d.clearanceIn)} in`;
    return c.fit?.status === "satisfied" ? "fits" : "does not fit";
  };
  const reqWords = (c: ProductCandidate) => c.matches.filter((m) => m.status === "conflict").map((m) => short(m.detail ?? m.requirementId)).join("; ");
  return (
    <section className="sc-card">
      <h3>{title}</h3>
      <ul className="sc-list">
        {good.map((c) => { const p = bySku(c.sku); return <li key={c.sku}><Dot s="satisfied" /><Thumb p={p} /><span className="sc-grow"><a href={p?.url} target="_blank" rel="noreferrer">{p?.name ?? c.sku}</a><span className="sc-why">{fitWords(c)}{c.candidate.counts.unknown ? `; ${c.candidate.counts.unknown} unresolved` : ""}</span></span><span className="sc-amt">{p?.priceCents != null ? formatCents(p.priceCents) : ""}</span></li>; })}
        {good.length === 0 && <li><Dot s="conflict" /><span>Nothing on this site both fits and meets the cabinet requirements.</span></li>}
      </ul>
      {rest.length > 0 && (
        <details className="sc-more"><summary>Why the others don't ({rest.length})</summary>
          <ul className="sc-list">{rest.map((c) => { const p = bySku(c.sku); const bad = [c.fit?.status === "conflict" || c.fit?.status === "unknown" ? fitWords(c) : "", reqWords(c.candidate)].filter(Boolean).join("; "); return <li key={c.sku}><Dot s={c.fit?.status === "unknown" && !c.candidate.counts.conflict ? "unknown" : "conflict"} /><span>{p?.name ?? c.sku}: {bad}</span></li>; })}</ul>
        </details>
      )}
    </section>
  );
}
